/**
 * GuestLayout — premium layout for Patient Portal
 * Handles boot/rehydration states properly: shows spinner while rehydrating,
 * redirects to landing if no session after boot completes.
 */
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useGuestSession } from '../contexts/GuestContext';
import { Home, Car, Hotel, Map, MessageSquare, LogOut, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { t } from '../i18n/en';
import { cn } from '@/lib/utils';
import InlineErrorBoundary from '@/components/system/InlineErrorBoundary';
import brandLogo from '@/assets/khmer-health-logo.png';
import GuestIcon3D, { VARIANT_TINT, type IconVariant } from './GuestIcon3D';
import { portalPath } from '@/shared/app/portalPath';
import { GUEST_PORTAL_FEATURES } from '@/shared/config/platform-config';

const NAV_DEFS = [
  { path: '/dashboard', label: t.nav.overview, icon: Home, variant: 'info' as IconVariant },
  { path: '/transport', label: t.nav.transport, icon: Car, variant: 'transport' as IconVariant },
  { path: '/stays', label: t.nav.hotels, icon: Hotel, variant: 'hotel' as IconVariant },
  { path: '/tours', label: t.nav.tours, icon: Map, variant: 'tour' as IconVariant },
  { path: '/requests', label: t.nav.requests, icon: MessageSquare, variant: 'request' as IconVariant },
];

export default function GuestLayout() {
  const { session, logout, bootState } = useGuestSession();
  const location = useLocation();
  const p = portalPath('guest');
  const NAV = NAV_DEFS
    .filter(n => n.path !== '/requests' || GUEST_PORTAL_FEATURES.changeRequests)
    .map(n => ({ ...n, path: p(n.path) }));

  // Still rehydrating from sessionStorage — show loading
  if (bootState === 'initializing') {
    return (
      <div className="min-h-screen bg-[hsl(var(--muted))] flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
        <p className="text-muted-foreground text-sm font-medium">Restoring your session…</p>
      </div>
    );
  }

  // Boot complete but no session — redirect to landing
  if (!session) {
    return <Navigate to={p('/')} replace />;
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--muted))] flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-[hsl(210,100%,15%)] to-[hsl(215,95%,25%)] text-white px-4 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <img
            src={brandLogo}
            alt="Khmer Health"
            className="h-8 sm:h-9 w-auto object-contain shrink-0"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <div>
            <p className="text-sm text-white/80">{t.hello}, {session.patientName}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={logout}
          className="text-white/90 hover:text-white hover:bg-white/10 gap-1.5"
        >
          <LogOut className="h-4 w-4" />
          <span className="hidden sm:inline">{t.logout}</span>
        </Button>
      </header>

      {/* Navigation with vibrant icons */}
      <nav className="bg-background border-b border-border px-1 py-2 flex gap-0.5 overflow-x-auto md:justify-center">
        {NAV.map(item => {
          const active = location.pathname === item.path;
          const Icon = item.icon;
          const tint = VARIANT_TINT[item.variant];
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex flex-col items-center gap-1.5 px-3 py-2 rounded-xl transition-all duration-200 min-w-[68px]',
                active ? 'bg-[hsl(var(--muted))]' : 'hover:bg-[hsl(var(--muted))]/50'
              )}
            >
              {active ? (
                <GuestIcon3D icon={Icon} variant={item.variant} size="sm" />
              ) : (
                <div className={cn('h-9 w-9 rounded-xl flex items-center justify-center transition-colors', tint)}>
                  <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                </div>
              )}
              <span
                className={cn(
                  'text-[11px] whitespace-nowrap transition-colors',
                  active ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'
                )}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* Content */}
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-5">
        <InlineErrorBoundary title="Patient page crashed">
          <Outlet />
        </InlineErrorBoundary>
      </main>

      {/* Footer */}
      <footer className="text-center text-xs text-muted-foreground py-4 px-4">
        {t.support247}
      </footer>
    </div>
  );
}
